/**
 * FormulaAutocomplete Component
 * Story 6.4: Implement Column Formulas
 * AC-6.4.4: Autocomplete support for column names and HyperFormula functions
 *
 * Shows suggestions when typing:
 * - `[` triggers column name suggestions in [ColumnName] format
 * - Function names trigger HyperFormula function suggestions
 */

import React, { useState, useEffect, useMemo, useRef } from 'react';

export interface AutocompleteItem {
  type: 'column' | 'function';
  label: string;
  value: string;
  description?: string;
}

export interface FormulaAutocompleteProps {
  /** Current input value */
  inputValue: string;
  /** Cursor position in input */
  cursorPosition: number;
  /** Available columns for suggestions */
  columns: { id: string; label: string }[];
  /** Callback when suggestion is selected */
  onSelect: (item: AutocompleteItem, startPos: number, endPos: number) => void;
  /** Callback to close autocomplete */
  onClose: () => void;
  /** Position for dropdown */
  position?: { top: number; left: number };
}

/**
 * Common HyperFormula functions for autocomplete
 * Subset of the 400+ available functions
 */
const COMMON_FUNCTIONS: AutocompleteItem[] = [
  // Math functions
  { type: 'function', label: 'SUM', value: 'SUM(', description: 'Adds numbers together' },
  { type: 'function', label: 'AVERAGE', value: 'AVERAGE(', description: 'Calculates the average' },
  { type: 'function', label: 'MIN', value: 'MIN(', description: 'Returns the minimum value' },
  { type: 'function', label: 'MAX', value: 'MAX(', description: 'Returns the maximum value' },
  { type: 'function', label: 'COUNT', value: 'COUNT(', description: 'Counts numeric values' },
  { type: 'function', label: 'COUNTA', value: 'COUNTA(', description: 'Counts non-empty values' },
  { type: 'function', label: 'ABS', value: 'ABS(', description: 'Returns absolute value' },
  { type: 'function', label: 'ROUND', value: 'ROUND(', description: 'Rounds to decimal places' },
  { type: 'function', label: 'ROUNDUP', value: 'ROUNDUP(', description: 'Rounds up' },
  { type: 'function', label: 'ROUNDDOWN', value: 'ROUNDDOWN(', description: 'Rounds down' },
  { type: 'function', label: 'CEILING', value: 'CEILING(', description: 'Rounds up to nearest multiple' },
  { type: 'function', label: 'FLOOR', value: 'FLOOR(', description: 'Rounds down to nearest multiple' },
  { type: 'function', label: 'MOD', value: 'MOD(', description: 'Returns remainder' },
  { type: 'function', label: 'POWER', value: 'POWER(', description: 'Raises to power' },
  { type: 'function', label: 'SQRT', value: 'SQRT(', description: 'Square root' },

  // Logic functions
  { type: 'function', label: 'IF', value: 'IF(', description: 'Conditional logic' },
  { type: 'function', label: 'IFS', value: 'IFS(', description: 'Multiple conditions' },
  { type: 'function', label: 'AND', value: 'AND(', description: 'Returns TRUE if all true' },
  { type: 'function', label: 'OR', value: 'OR(', description: 'Returns TRUE if any true' },
  { type: 'function', label: 'NOT', value: 'NOT(', description: 'Inverts boolean' },
  { type: 'function', label: 'IFERROR', value: 'IFERROR(', description: 'Returns value if error' },
  { type: 'function', label: 'IFNA', value: 'IFNA(', description: 'Returns value if #N/A' },
  { type: 'function', label: 'SWITCH', value: 'SWITCH(', description: 'Switch statement' },

  // Text functions
  { type: 'function', label: 'CONCATENATE', value: 'CONCATENATE(', description: 'Joins text' },
  { type: 'function', label: 'CONCAT', value: 'CONCAT(', description: 'Joins text (modern)' },
  { type: 'function', label: 'LEFT', value: 'LEFT(', description: 'Left characters' },
  { type: 'function', label: 'RIGHT', value: 'RIGHT(', description: 'Right characters' },
  { type: 'function', label: 'MID', value: 'MID(', description: 'Middle characters' },
  { type: 'function', label: 'LEN', value: 'LEN(', description: 'Text length' },
  { type: 'function', label: 'TRIM', value: 'TRIM(', description: 'Remove extra spaces' },
  { type: 'function', label: 'UPPER', value: 'UPPER(', description: 'Convert to uppercase' },
  { type: 'function', label: 'LOWER', value: 'LOWER(', description: 'Convert to lowercase' },
  { type: 'function', label: 'TEXT', value: 'TEXT(', description: 'Format as text' },

  // Date functions
  { type: 'function', label: 'TODAY', value: 'TODAY()', description: 'Current date' },
  { type: 'function', label: 'NOW', value: 'NOW()', description: 'Current date/time' },
  { type: 'function', label: 'DATE', value: 'DATE(', description: 'Create date' },
  { type: 'function', label: 'YEAR', value: 'YEAR(', description: 'Get year' },
  { type: 'function', label: 'MONTH', value: 'MONTH(', description: 'Get month' },
  { type: 'function', label: 'DAY', value: 'DAY(', description: 'Get day' },
  { type: 'function', label: 'DATEDIF', value: 'DATEDIF(', description: 'Date difference' },
  { type: 'function', label: 'EDATE', value: 'EDATE(', description: 'Add months to date' },
  { type: 'function', label: 'EOMONTH', value: 'EOMONTH(', description: 'End of month' },
  { type: 'function', label: 'WEEKDAY', value: 'WEEKDAY(', description: 'Day of week' },

  // Custom functions
  { type: 'function', label: 'CHILDREN', value: 'CHILDREN()', description: 'Sum of child row values (hierarchical)' },
];

/**
 * Find the token being typed at cursor position
 */
function findCurrentToken(value: string, cursorPos: number): { token: string; start: number; end: number; type: 'column' | 'function' } | null {
  // Look backwards from cursor for [ to detect column reference
  let bracketStart = -1;
  for (let i = cursorPos - 1; i >= 0; i--) {
    if (value[i] === '[') {
      bracketStart = i;
      break;
    }
    if (value[i] === ']' || value[i] === '=' || value[i] === '(' || value[i] === ',') {
      break;
    }
  }

  if (bracketStart >= 0) {
    // We're inside a column reference [...]
    const token = value.slice(bracketStart + 1, cursorPos);
    return {
      token,
      start: bracketStart,
      end: cursorPos,
      type: 'column',
    };
  }

  // Look for function name (letters before cursor, after operator/delimiter)
  let funcStart = cursorPos;
  for (let i = cursorPos - 1; i >= 0; i--) {
    const char = value[i];
    if (/[A-Za-z_]/.test(char)) {
      funcStart = i;
    } else {
      break;
    }
  }

  if (funcStart < cursorPos) {
    const token = value.slice(funcStart, cursorPos);
    // Only show function autocomplete if token is at least 2 chars
    if (token.length >= 2) {
      return {
        token,
        start: funcStart,
        end: cursorPos,
        type: 'function',
      };
    }
  }

  return null;
}

/**
 * FormulaAutocomplete - Dropdown for formula suggestions
 */
export function FormulaAutocomplete({
  inputValue,
  cursorPosition,
  columns,
  onSelect,
  onClose,
  position,
}: FormulaAutocompleteProps): React.ReactElement | null {
  const [selectedIndex, setSelectedIndex] = useState(0);
  const listRef = useRef<HTMLDivElement>(null);

  // Find current token and filter suggestions
  const { suggestions, tokenInfo } = useMemo(() => {
    const info = findCurrentToken(inputValue, cursorPosition);
    if (!info) {
      return { suggestions: [], tokenInfo: null };
    }

    const filterText = info.token.toUpperCase();

    if (info.type === 'column') {
      // Filter columns
      const columnSuggestions: AutocompleteItem[] = columns
        .filter(col => col.label.toUpperCase().includes(filterText))
        .map(col => ({
          type: 'column' as const,
          label: col.label,
          value: `[${col.label}]`,
          description: 'Column reference',
        }));
      return { suggestions: columnSuggestions, tokenInfo: info };
    }

    // Filter functions
    const funcSuggestions = COMMON_FUNCTIONS.filter(func =>
      func.label.includes(filterText)
    );
    return { suggestions: funcSuggestions, tokenInfo: info };
  }, [inputValue, cursorPosition, columns]);

  // Reset selection when suggestions change
  useEffect(() => {
    setSelectedIndex(0);
  }, [suggestions.length]);

  // Handle keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (suggestions.length === 0) return;

      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          setSelectedIndex(prev => Math.min(prev + 1, suggestions.length - 1));
          break;
        case 'ArrowUp':
          e.preventDefault();
          setSelectedIndex(prev => Math.max(prev - 1, 0));
          break;
        case 'Enter':
        case 'Tab':
          e.preventDefault();
          if (suggestions[selectedIndex] && tokenInfo) {
            onSelect(suggestions[selectedIndex], tokenInfo.start, tokenInfo.end);
          }
          break;
        case 'Escape':
          e.preventDefault();
          onClose();
          break;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [suggestions, selectedIndex, tokenInfo, onSelect, onClose]);

  // Scroll selected item into view
  useEffect(() => {
    if (listRef.current) {
      const selected = listRef.current.children[selectedIndex] as HTMLElement;
      if (selected) {
        selected.scrollIntoView({ block: 'nearest' });
      }
    }
  }, [selectedIndex]);

  // Don't render if no suggestions
  if (suggestions.length === 0 || !tokenInfo) {
    return null;
  }

  return (
    <div
      className="formula-autocomplete absolute z-50 bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 border border-slate-200 dark:border-slate-600 rounded-md shadow-lg min-w-[200px] max-w-[300px] max-h-[200px] overflow-y-auto"
      style={position ? { top: position.top, left: position.left } : undefined}
      data-testid="formula-autocomplete"
    >
      <div ref={listRef}>
        {suggestions.map((item, index) => (
          <div
            key={`${item.type}-${item.label}`}
            className={`
              px-3 py-2 cursor-pointer flex items-center justify-between
              ${index === selectedIndex ? 'bg-blue-100 dark:bg-blue-800' : 'hover:bg-slate-100 dark:hover:bg-slate-700'}
            `}
            onClick={() => {
              if (tokenInfo) {
                onSelect(item, tokenInfo.start, tokenInfo.end);
              }
            }}
            onMouseEnter={() => setSelectedIndex(index)}
          >
            <div className="flex items-center gap-2">
              <span
                className={`
                  text-xs px-1.5 py-0.5 rounded font-medium
                  ${item.type === 'column'
                    ? 'bg-purple-100 dark:bg-purple-900 text-purple-700 dark:text-purple-300'
                    : 'bg-green-100 dark:bg-green-900 text-green-700 dark:text-green-300'
                  }
                `}
              >
                {item.type === 'column' ? 'Col' : 'Fn'}
              </span>
              <span className="font-mono text-sm">{item.label}</span>
            </div>
            {item.description && (
              <span className="text-xs text-slate-500 dark:text-slate-400 ml-2 truncate max-w-[100px]">
                {item.description}
              </span>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

export default FormulaAutocomplete;
